// HistorySection.tsx
import { Table, Tag, Card, Row, Col, Empty, Spin } from "antd";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useTranslation } from "react-i18next";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

interface HistoryRecord {
  0: string; // date
  1: string; // platform
  2: string; // id
  3: string; // operation
  4: string; // amount
  5: string; // previousBalance
  6: string; // newBalance
  7: number; // record id
}

interface DataByHour {
  [key: string]: number;
}

interface HistorySectionProps {
  username: string;
  isUpdating?: boolean;
  historyData?: any[];
}

export const HistorySection: React.FC<HistorySectionProps> = ({ isUpdating = false, historyData }) => {
  const { t } = useTranslation();

  if (isUpdating) {
    return (
      <div style={{ width: '100%', textAlign: 'center', padding: '20px' }}>
        <Spin tip="Actualizando historial..." />
      </div>
    );
  }

  if (!historyData || !Array.isArray(historyData) || historyData.length === 0) {
    return <Empty description={t("users.historySection.emptyState")} />;
  }

  const formatAmount = (amount: string): number => {
    try {
      return parseFloat(amount.replace(/\./g, "").replace(",", ".")) || 0;
    } catch {
      return 0;
    }
  };

  const safeData = historyData.filter((item): item is HistoryRecord => Array.isArray(item) && item.length >= 8);

  const hourlyData = safeData.reduce((acc: DataByHour, item) => {
    try {
      const date = new Date(item[0]);
      const hourKey = `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:00`;
      if (!acc[hourKey]) acc[hourKey] = 0;
      const amount = formatAmount(item[4]);
      acc[hourKey] += amount;
      return acc;
    } catch {
      return acc;
    }
  }, {});

  const chartData = {
    labels: Object.keys(hourlyData).slice(-10).reverse(),
    datasets: [
      {
        label: t("users.historySection.charts.bets"), //'Bets',
        data: safeData
          .filter((item) => item[3] === "Bet")
          .reduce((acc: DataByHour, item) => {
            const date = new Date(item[0]);
            const hourKey = `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:00`;
            if (!acc[hourKey]) acc[hourKey] = 0;
            acc[hourKey] += formatAmount(item[4]);
            return acc;
          }, {}),
        borderColor: "#ff4d4f",
        backgroundColor: "rgba(255, 77, 79, 0.1)",
        tension: 0.1,
        fill: true,
      },
      {
        label: t("users.historySection.charts.wins"), //'Wins',
        data: safeData
          .filter((item) => item[3] === "Win")
          .reduce((acc: DataByHour, item) => {
            const date = new Date(item[0]);
            const hourKey = `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:00`;
            if (!acc[hourKey]) acc[hourKey] = 0;
            acc[hourKey] += formatAmount(item[4]);
            return acc;
          }, {}),
        borderColor: "#52c41a",
        backgroundColor: "rgba(82, 196, 26, 0.1)",
        tension: 0.1,
        fill: true,
      },
      {
        label: t("users.historySection.charts.deposits"), //'Deposits',
        data: safeData
          .filter((item) => item[3] === "Credit Add")
          .reduce((acc: DataByHour, item) => {
            const date = new Date(item[0]);
            const hourKey = `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:00`;
            if (!acc[hourKey]) acc[hourKey] = 0;
            acc[hourKey] += formatAmount(item[4]);
            return acc;
          }, {}),
        borderColor: "#1890ff",
        backgroundColor: "rgba(24, 144, 255, 0.1)",
        tension: 0.1,
        fill: true,
      },
      {
        label: t("users.historySection.charts.withdrawals"), //'Withdrawals',
        data: safeData
          .filter((item) => item[3] === "Credit Out")
          .reduce((acc: DataByHour, item) => {
            const date = new Date(item[0]);
            const hourKey = `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:00`;
            if (!acc[hourKey]) acc[hourKey] = 0;
            acc[hourKey] += Math.abs(formatAmount(item[4]));
            return acc;
          }, {}),
        borderColor: "#faad14",
        backgroundColor: "rgba(250, 173, 20, 0.1)",
        tension: 0.1,
        fill: true,
      },
    ],
  };

  const platformData = {
    labels: Array.from(new Set(safeData.map((item) => item[1]))),
    datasets: [
      {
        label: t("users.historySection.charts.operationsByPlatform"), //'Operations by Platform',
        data: Array.from(
          safeData.reduce((acc: Map<string, number>, item) => {
            const platform = item[1];
            return acc.set(platform, (acc.get(platform) || 0) + 1);
          }, new Map())
        ).map(([_, count]) => count),
        backgroundColor: ["#ff4d4f", "#52c41a", "#1890ff"],
      },
    ],
  };

  const columns = [
    {
      title: t("users.historySection.columns.date"),
      dataIndex: "0",
      key: "date",
      render: (date: string) => {
        try {
          return new Date(date).toLocaleString();
        } catch {
          return "Invalid Date";
        }
      },
    },
    {
      title: t("users.historySection.columns.platform"),
      dataIndex: "1",
      key: "platform",
      render: (platform: string) => (
        <Tag color={platform === "System" ? "blue" : "green"}>
          {platform === "System"
            ? t("users.historySection.operations.system")
            : platform}
        </Tag>
      ),
    },
    {
      title: t("users.historySection.columns.operation"),
      dataIndex: "3",
      key: "operation",
      render: (operation: string) => (
        <Tag
          color={
            operation === "Bet"
              ? "red"
              : operation === "Win"
              ? "green"
              : operation === "Credit Add"
              ? "blue"
              : operation === "Credit Out"
              ? "volcano"
              : "default"
          }
        >
          {t(
            "users.historySection.operations." +
              operation.charAt(0).toLowerCase() +
              operation.slice(1).replace(/\s+/g, "")
          )}
        </Tag>
      ),
    },
    {
      title: t("users.historySection.columns.amount"),
      dataIndex: "4",
      key: "amount",
      render: (amount: string) => {
        const value = formatAmount(amount);
        return (
          <span
            style={{
              color: value >= 0 ? "#52c41a" : "#f5222d",
              fontWeight: 500,
            }}
          >
            $
            {Math.abs(value).toLocaleString("es-AR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      },
    },
    {
      title: t("users.historySection.columns.previousBalance"),
      dataIndex: "5",
      key: "previousBalance",
      render: (balance: string) => {
        const value = formatAmount(balance);
        return (
          <span
            style={{
              color: value >= 0 ? "#1890ff" : "#f5222d",
              fontWeight: 500,
            }}
          >
            $
            {value.toLocaleString("es-AR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      },
    },
    {
      title: t("users.historySection.columns.newBalance"),
      dataIndex: "6",
      key: "newBalance",
      render: (balance: string) => {
        const value = formatAmount(balance);
        return (
          <span
            style={{
              color: value >= 0 ? "#1890ff" : "#f5222d",
              fontWeight: 500,
            }}
          >
            $
            {value.toLocaleString("es-AR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={t("users.historySection.charts.transactionSummary")} variant="outlined">
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top" as const,
                    labels: {
                      boxWidth: 12,
                      padding: 8,
                      font: {
                        size: 11,
                      },
                    },
                  },
                  tooltip: {
                    mode: "index" as const,
                    intersect: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => `$${Number(value).toLocaleString("es-AR")}`,
                      font: {
                        size: 10,
                      },
                    },
                  },
                  x: {
                    ticks: {
                      maxRotation: 45,
                      minRotation: 45,
                      font: {
                        size: 10,
                      },
                    },
                  },
                },
                interaction: {
                  mode: "nearest" as const,
                  axis: "x" as const,
                  intersect: false,
                },
              }}
              style={{ height: "300px" }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("users.historySection.charts.operationsByPlatform")} variant="outlined">
            <Bar
              data={platformData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: "y" as const,
                plugins: {
                  legend: {
                    display: false,
                  },
                },
                scales: {
                  x: {
                    beginAtZero: true,
                    ticks: {
                      precision: 0,
                      font: {
                        size: 10,
                      },
                    },
                  },
                  y: {
                    ticks: {
                      font: {
                        size: 10,
                      },
                    },
                  },
                },
              }}
              style={{ height: "300px" }}
            />
          </Card>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={safeData}
        rowKey={(record) => record[7]}
        size="small"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `${t("users.historySection.pagination.total")}: ${total} ${t("users.historySection.pagination.registers")}`,
          locale: {
            items_per_page: t("users.historySection.pagination.itemsPerPage"),
          }
        }}
        scroll={{ x: true }}
        style={{ marginTop: 16 }}
      />
    </div>
  );
};
